import React, { useState, useCallback, useEffect } from 'react';
import IdentityMigrationModalContents from './IdentityMigrationModalContents';
import {
  setIdentityModalDismissed,
  shouldShowIdentityModal,
  isIdentityModalDismissible,
} from './utils';
import { useStore } from '~/src/hooks/useMst';
import { useGenerateIdentityMigrationLink } from '../../hooks/useGenerateIdentityMigrationLink';
import useFeatureAccess from '~/src/hooks/useFeatureAccesses';
import analyticsService from '~/src/services/analytics';

const IdentityMigrationModalContainer = () => {
  const store = useStore();
  const { isIdentityLoginDisabled } = useFeatureAccess();
  const { user } = store;
  const generateMigrationLink = useGenerateIdentityMigrationLink();

  const selectedOrgFprint = user.currentOrganization?.fprint || '';
  const clioTokenPresenceByOrg = user.profile?.clioTokenPresenceByOrg || {};
  const orgCreatedAt = user.currentOrganization?.orgCreatedAt || '1970-01-01';
  const orgIsInternal = user.currentOrganization?.orgIsInternal || false;
  const isIdentityAuthenticated = user.profile?.identityAuthenticated || false;
  const identityInviteFprint = user.profile?.identityInviteFprint || '';
  const userEmail = user.currentOrganization?.email || '';

  const canCloseModal = isIdentityModalDismissible();
  const shouldShowModal = shouldShowIdentityModal({
    identityInviteFprint,
    isIdentityAuthenticated,
    isIdentityLoginDisabled,
    selectedOrgFprint,
    clioTokenPresenceByOrg,
    orgCreatedAt,
    orgIsInternal,
    userEmail,
  });

  const [isOpen, setIsOpen] = useState(shouldShowModal);
  const [isMigrationLinkGenerationError, setIsMigrationLinkGenerationError] =
    useState(false);

  useEffect(() => {
    setIsOpen(shouldShowModal);
  }, [shouldShowModal]);

  const handleClose = useCallback(() => {
    analyticsService.track('Identity Modal Dismissed');
    setIdentityModalDismissed();
    setIsOpen(false);
  }, []);

  const handleMigrate = useCallback(async () => {
    analyticsService.track('Identity Modal Clicked');
    setIsMigrationLinkGenerationError(false);
    try {
      const result = await generateMigrationLink.mutateAsync();
      window.location.href = result.accept_url;
    } catch (error) {
      console.error('Failed to generate migration link:', error);
      setIsMigrationLinkGenerationError(true);
    }
  }, [generateMigrationLink]);

  return (
    <IdentityMigrationModalContents
      open={isOpen}
      onClose={handleClose}
      canClose={canCloseModal}
      handleMigrate={handleMigrate}
      isError={isMigrationLinkGenerationError}
      email={userEmail}
    />
  );
};

export default IdentityMigrationModalContainer;
